import { ChangeDetectionStrategy, Component, computed, input, InputSignal, Signal } from '@angular/core';
import { SzScatterRun } from './sz-scatter-trend.types';

interface DotRender {
  readonly cx: number;
  readonly cy: number;
  readonly opacity: number;
  readonly isPb: boolean;
}

interface ScaledChart {
  readonly dots: readonly DotRender[];
  readonly trendX1: number;
  readonly trendY1: number;
  readonly trendX2: number;
  readonly trendY2: number;
  readonly bestLineY: number;
  readonly yLabels: readonly { y: number; text: string }[];
}

const VIEW_W: number = 360;
const VIEW_H: number = 220;
const PAD_L: number = 34;
const PAD_R: number = 16;
const PAD_T: number = 18;
const PAD_B: number = 32;

@Component({
  selector: 'sz-scatter-trend',
  templateUrl: './sz-scatter-trend.component.html',
  styleUrls: ['./sz-scatter-trend.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})
export class SzScatterTrendComponent {
  readonly runs: InputSignal<readonly SzScatterRun[]> = input.required<readonly SzScatterRun[]>();
  readonly lowerIsBetter: InputSignal<boolean> = input<boolean>(true);
  /** Optional fixed y-range; if not provided, derived from data. */
  readonly yMin: InputSignal<number | null> = input<number | null>(null);
  readonly yMax: InputSignal<number | null> = input<number | null>(null);
  readonly yUnit: InputSignal<string> = input<string>('');
  readonly leftLabel: InputSignal<string> = input<string>('');
  readonly rightLabel: InputSignal<string> = input<string>('today');

  readonly viewBox: string = `0 0 ${VIEW_W} ${VIEW_H}`;
  readonly viewW: number = VIEW_W;
  readonly viewH: number = VIEW_H;
  readonly padL: number = PAD_L;
  readonly padB: number = PAD_B;

  readonly chart: Signal<ScaledChart | null> = computed((): ScaledChart | null => {
    const runs: readonly SzScatterRun[] = this.runs();
    if (runs.length === 0) return null;
    const values: number[] = runs.map((r: SzScatterRun): number => r.value);
    const minY: number = this.yMin() ?? Math.min(...values);
    const maxY: number = this.yMax() ?? Math.max(...values);
    const sessionMin: number = Math.min(...runs.map((r: SzScatterRun): number => r.sessionIndex));
    const sessionMax: number = Math.max(...runs.map((r: SzScatterRun): number => r.sessionIndex));
    const sessionRange: number = sessionMax - sessionMin || 1;
    const yRange: number = maxY - minY || 1;

    const plotW: number = VIEW_W - PAD_L - PAD_R;
    const plotH: number = VIEW_H - PAD_T - PAD_B;

    const toX = (sessionIdx: number, jitterIdx: number): number =>
      PAD_L + ((sessionIdx - sessionMin) / sessionRange) * plotW + (jitterIdx % 6) * 4;
    const toY = (value: number): number => PAD_T + ((maxY - value) / yRange) * plotH;

    const bestValue: number = this.lowerIsBetter() ? Math.min(...values) : Math.max(...values);

    const dots: DotRender[] = runs.map((r: SzScatterRun, i: number): DotRender => {
      const t: number = (r.sessionIndex - sessionMin) / sessionRange;
      return {
        cx: toX(r.sessionIndex, i),
        cy: toY(r.value),
        opacity: 0.55 + t * 0.35,
        isPb: r.value === bestValue
      };
    });

    // Simple linear regression for trend line
    const n: number = runs.length;
    const sumX: number = runs.reduce((acc: number, r: SzScatterRun): number => acc + r.sessionIndex, 0);
    const sumY: number = runs.reduce((acc: number, r: SzScatterRun): number => acc + r.value, 0);
    const sumXY: number = runs.reduce((acc: number, r: SzScatterRun): number => acc + r.sessionIndex * r.value, 0);
    const sumXX: number = runs.reduce(
      (acc: number, r: SzScatterRun): number => acc + r.sessionIndex * r.sessionIndex,
      0
    );
    const denom: number = n * sumXX - sumX * sumX || 1;
    const slope: number = (n * sumXY - sumX * sumY) / denom;
    const intercept: number = (sumY - slope * sumX) / n;
    const trendY1: number = toY(intercept + slope * sessionMin);
    const trendY2: number = toY(intercept + slope * sessionMax);

    return {
      dots,
      trendX1: PAD_L,
      trendY1,
      trendX2: VIEW_W - PAD_R,
      trendY2,
      bestLineY: toY(bestValue),
      yLabels: [
        { y: PAD_T, text: `${maxY.toFixed(2)}${this.yUnit()}` },
        { y: PAD_T + plotH * 0.5, text: `${((maxY + minY) / 2).toFixed(2)}${this.yUnit()}` },
        { y: PAD_T + plotH, text: `${minY.toFixed(2)}${this.yUnit()}` }
      ]
    };
  });
}
